import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { masterApi } from "@/lib/api";
import { useUnit } from "@/pages/Master/MasterApp";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { modal } from "@/lib/modal";
import { protegeDoModalGlobal } from "@/lib/dialogModal";
import { inserirNoCursor } from "@/lib/indicacaoWeb";
import {
  agruparPorCanal, origemDoItem, textoDoNivel, textoSemPersonalizacao, descricaoVoltarPadrao,
  variaveisDoItem, validarTexto, smsInfo, erroApiMensagens, limiteTexto, gruposObrigatorios, listaVars,
  textoTamanhoSms, provedorWhatsApp, ORIGEM_LBL, CANAL_LBL, unidadesComTextoProprio, textoUnidadesProprias,
} from "@/lib/mensagensAuto";
import { Pencil, Lock, RotateCcw, AlertTriangle, Info, Save, MessageSquareText, MessageCircle, Link2 } from "lucide-react";
import EnvioMensagens from "@/pages/Master/EnvioMensagens";

/**
 * Fase 5 (pedido de 19/10, item 7) — Master > Sistema > Mensagens automáticas.
 * Cada mensagem que o sistema envia sozinho (SMS, WhatsApp pelo provedor antigo e textos dos links
 * wa.me) com texto editável por unidade: unidade > padrão editado > padrão do sistema. O SMS do
 * código de validação (OTP) é fixo (aprovado pela Allcance). No WhatsApp oficial sai o template
 * aprovado pela Meta. Contrato: GET/PUT /master/mensagens e POST /master/mensagens/preview
 * (detalhes em lib/mensagensAuto.js).
 */

const ICONE_CANAL = { sms: MessageSquareText, whatsapp: MessageCircle, wame: Link2 };
const ORIGEM_CLS = { unidade: "chip-green", global: "chip-yellow", padrao: "chip-gray" };
const selectCls = "mt-1 w-full sm:w-[280px] h-10 rounded-lg bg-white border border-neutral-200 px-3 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition";

export default function MasterMensagens() {
  const { unitId: unitTopo, unidades = [] } = useUnit() || {};
  const [params] = useSearchParams();
  const inicial = params.get("unidade") || (unitTopo && unitTopo !== "all" ? unitTopo : "");
  const chaveDestaque = params.get("chave") || "";   // aberta por atalho de outra tela
  const [unidade, setUnidade] = useState(inicial);   // "" = padrão do sistema
  const [itens, setItens] = useState(null);
  const [erro, setErro] = useState("");
  const [editando, setEditando] = useState(null);     // item em edição
  const [salvando, setSalvando] = useState(false);
  const [wa, setWa] = useState(null);                 // WhatsApp em uso agora (null = não deu para saber)
  const seq = useRef(0);

  // Qual WhatsApp está em uso (o mesmo status da tela do WhatsApp). Com a API oficial os textos de
  // WhatsApp daqui ficam sem efeito; falha aqui só deixa o aviso genérico.
  useEffect(() => {
    let vivo = true;
    Promise.resolve()
      .then(() => masterApi.get("/master/whatsapp/oficial/status"))
      .then((r) => { if (vivo) setWa(provedorWhatsApp(r?.data)); })
      .catch(() => { if (vivo) setWa(null); });
    return () => { vivo = false; };
  }, []);

  // O seletor do topo também troca a unidade desta tela ("Todas" = padrão do sistema). Só quando
  // ele MUDA: ao abrir, vale o ?unidade= do link (vindo de outra tela).
  const topoAnterior = useRef(unitTopo);
  useEffect(() => {
    if (topoAnterior.current === unitTopo) return;
    topoAnterior.current = unitTopo;
    setUnidade(unitTopo && unitTopo !== "all" ? unitTopo : "");
  }, [unitTopo]);

  const carregar = async () => {
    const minha = ++seq.current;
    try {
      const { data } = await masterApi.get(`/master/mensagens?unit_id=${encodeURIComponent(unidade)}`);
      if (minha !== seq.current) return;
      setItens(Array.isArray(data?.itens) ? data.itens : []);
      setErro("");
    } catch (e) {
      if (minha !== seq.current) return;
      setItens([]);
      setErro(erroApiMensagens(e));
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setItens(null); carregar(); }, [unidade]);

  // Atalho de outra tela (?chave=): rola até a mensagem, que fica destacada.
  const carregado = Array.isArray(itens) && itens.length > 0;
  useEffect(() => {
    if (!carregado || !chaveDestaque) return;
    const el = document.querySelector(`[data-chave="${chaveDestaque.replace(/[^\w-]/g, "")}"]`);
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [carregado, chaveDestaque]);

  const nomeUnidade = unidade ? (unidades.find((u) => u.id === unidade)?.nome || "Unidade") : "";
  const nivelLbl = unidade ? `Unidade ${nomeUnidade}` : "Padrão do sistema (todas as unidades sem texto próprio)";

  const salvar = async (item, texto) => {
    setSalvando(true);
    try {
      await masterApi.put("/master/mensagens", { unit_id: unidade || null, chave: item.chave, canal: item.canal, texto });
      await carregar();
      return true;
    } catch (e) {
      modal.error(erroApiMensagens(e));
      return false;
    } finally { setSalvando(false); }
  };

  const voltarPadrao = async (item) => {
    const ok = await modal.confirm({
      title: "Voltar ao padrão",
      message: unidade
        ? "O texto próprio desta unidade será apagado. A mensagem passa a usar o texto abaixo."
        : "O padrão editado será apagado e a mensagem volta ao texto original do sistema.",
      details: [
        { label: "Mensagem", value: item.rotulo || item.chave },
        { label: "Canal", value: CANAL_LBL[item.canal] || item.canal },
        { label: "Onde", value: unidade ? nomeUnidade : "Padrão do sistema" },
        { label: "Passa a usar", value: descricaoVoltarPadrao(item, unidade) },
      ],
      confirmText: "Voltar ao padrão",
    });
    if (!ok) return false;
    const feito = await salvar(item, "");
    if (feito) modal.success("A mensagem voltou ao padrão.");
    return feito;
  };

  // Visão "Padrão do sistema": o padrão não muda as unidades com texto próprio. Avisa e pede
  // confirmação antes de salvar (verificação final da Fase 5).
  const confirmaPadraoGlobal = async (item) => {
    const proprias = unidade ? [] : unidadesComTextoProprio(item);
    if (!proprias.length) return true;
    return modal.confirm({
      title: "Salvar o padrão do sistema",
      message: `${textoUnidadesProprias(proprias)} O padrão vale só para as unidades sem texto próprio. `
        + "Para mudar nelas, escolha a unidade em \"Editar textos de\".",
      details: [
        { label: "Mensagem", value: item.rotulo || item.chave },
        { label: "Não mudam", value: proprias.map((u) => u.unit_nome || u.unit_id).join(", ") },
      ],
      confirmText: "Salvar o padrão",
    });
  };

  const grupos = agruparPorCanal(itens || []);

  return (
    <div className="px-4 md:px-6 pt-4 pb-8 space-y-4 float-in" data-testid="master-mensagens">
      <div>
        <div className="chip mb-1.5 text-[10px] py-0.5">Sistema</div>
        <h1 className="font-serif-display text-2xl md:text-3xl leading-[1.1]">Mensagens automáticas</h1>
        <p className="text-[12px] text-neutral-500 mt-1">
          Textos que o sistema envia sozinho. Cada unidade pode ter o próprio texto; sem texto próprio vale o
          padrão do sistema. Mensagem em branco volta ao padrão.
        </p>
      </div>

      {/* Pedido de 26/10: liga/desliga, rota (SMS ou WhatsApp) e reserva de cada mensagem */}
      <EnvioMensagens />

      <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-[12px] text-amber-900"
           data-testid="msg-aviso-oficial">
        <AlertTriangle size={14} className="shrink-0 mt-0.5" />
        <span>
          {wa && (
            <span className="block mb-1" data-testid="msg-provedor">
              WhatsApp em uso agora: <b>{wa.rotulo}</b>.{" "}
              {wa.oficial && "Os textos de WhatsApp desta tela ficam sem efeito enquanto ela estiver em uso (sai o template aprovado). SMS e links wa.me continuam valendo."}
              {wa.simulacao && "Nenhuma mensagem de WhatsApp sai agora. SMS e links wa.me continuam valendo."}
              {wa.textosValem && "Os textos de WhatsApp desta tela são os que saem."}
            </span>
          )}
          <b>WhatsApp oficial (API da Meta):</b> a mensagem enviada é sempre o template aprovado pela Meta, só as
          variáveis mudam. O texto editado aqui vale para o SMS, para o WhatsApp pelo provedor antigo e para os links wa.me.
        </span>
      </div>

      <div className="card-soft rounded-xl p-3 flex flex-wrap items-end gap-3">
        <div className="min-w-0">
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Editar textos de</label>
          <select data-testid="msg-unidade" value={unidade} onChange={(e) => setUnidade(e.target.value)} className={selectCls}>
            <option value="">Padrão do sistema</option>
            {unidades.map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
          </select>
        </div>
        <p className="text-[11px] text-neutral-500 flex-1 min-w-[200px]">
          {unidade
            ? "Texto desta unidade > padrão editado > padrão do sistema. A unidade da mensagem é a do atendimento; sem atendimento, a do último abastecimento do cliente, senão a do cadastro."
            : "O padrão editado vale para todas as unidades que não têm texto próprio."}
        </p>
      </div>

      {erro && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-[12px] text-rose-800" data-testid="msg-erro">{erro}</div>
      )}
      {itens === null && <div className="text-center text-[12px] text-neutral-500 py-6">Carregando...</div>}
      {itens !== null && !erro && grupos.length === 0 && (
        <div className="card-soft rounded-xl p-5 text-center text-[12px] text-neutral-500" data-testid="msg-vazio">Nenhuma mensagem cadastrada.</div>
      )}

      {grupos.map((g) => {
        const Icone = ICONE_CANAL[g.id] || MessageSquareText;
        return (
          <section key={g.id} className="space-y-2" data-testid={`msg-grupo-${g.id}`}>
            <div className="flex items-center gap-2">
              <Icone size={15} className="text-[color:var(--brand-blue)]" />
              <h2 className="font-serif-display text-lg">{g.titulo}</h2>
            </div>
            {g.descricao && <p className="text-[11px] text-neutral-500 -mt-1">{g.descricao}</p>}
            <div className="space-y-2">
              {g.itens.map((it) => {
                const id = `${it.canal}-${it.chave}`;
                const origem = origemDoItem(it, unidade);
                const destaque = chaveDestaque && it.chave === chaveDestaque;
                const editavel = it.editavel !== false;
                const proprio = textoDoNivel(it, unidade);
                return (
                  <div key={id} data-chave={it.chave} data-testid={`msg-item-${id}`}
                       className={`card-soft rounded-xl p-3.5 space-y-2 scroll-mt-24 ${destaque ? "ring-2 ring-[color:var(--brand-blue)]" : ""}`}>
                    <div className="flex flex-wrap items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-semibold text-[color:var(--ink)]">{it.rotulo || it.chave}</div>
                        {it.gatilho && <div className="text-[11px] text-neutral-500 mt-0.5">{it.gatilho}</div>}
                      </div>
                      {it.canal === "whatsapp" && wa && !wa.textosValem && (
                        <span data-testid={`msg-sem-efeito-${id}`} title={`WhatsApp em uso: ${wa.rotulo}`}
                              className="chip chip-orange text-[10px] py-0.5 font-bold uppercase tracking-wide" style={{ borderRadius: 6 }}>
                          Sem efeito agora
                        </span>
                      )}
                      {editavel ? (
                        <span data-testid={`msg-origem-${id}`} className={`chip ${ORIGEM_CLS[origem]} text-[10px] py-0.5 font-bold uppercase tracking-wide`} style={{ borderRadius: 6 }}>
                          {ORIGEM_LBL[origem]}
                        </span>
                      ) : (
                        <span data-testid={`msg-fixo-${id}`} className="chip chip-gray text-[10px] py-0.5 font-bold uppercase tracking-wide inline-flex items-center gap-1" style={{ borderRadius: 6 }}>
                          <Lock size={10} /> Fixo
                        </span>
                      )}
                      {editavel && (
                        <div className="flex gap-1">
                          <button type="button" data-testid={`msg-editar-${id}`} onClick={() => setEditando(it)}
                                  title="Editar" className="icon-action icon-action--edit"><Pencil size={14} /></button>
                          {proprio && (
                            <button type="button" data-testid={`msg-padrao-${id}`} onClick={() => voltarPadrao(it)} disabled={salvando}
                                    title="Voltar ao padrão" className="icon-action icon-action--amber"><RotateCcw size={14} /></button>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-[12.5px] whitespace-pre-wrap text-neutral-800 break-words"
                         data-testid={`msg-texto-${id}`}>
                      {it.texto_efetivo || it.texto_padrao || "—"}
                    </div>
                    {!editavel && (
                      <p className="text-[11px] text-neutral-500" data-testid={`msg-fixo-motivo-${id}`}>
                        {it.observacao || "Texto aprovado pela Allcance (operadora de SMS): não pode ser alterado, senão o envio pode ficar retido."}
                      </p>
                    )}
                    {editavel && it.observacao && (
                      <p className="text-[11px] text-neutral-500" data-testid={`msg-obs-${id}`}>{it.observacao}</p>
                    )}
                    {it.oficial?.observacao && (
                      <p className="text-[11px] text-amber-800 flex items-start gap-1" data-testid={`msg-oficial-${id}`}>
                        <Info size={11} className="shrink-0 mt-0.5" /> {it.oficial.observacao}
                      </p>
                    )}
                    {!unidade && editavel && unidadesComTextoProprio(it).length > 0 && (
                      <p className="text-[11px] text-amber-800 flex items-start gap-1" data-testid={`msg-proprias-${id}`}>
                        <AlertTriangle size={11} className="shrink-0 mt-0.5" />
                        <span>{textoUnidadesProprias(unidadesComTextoProprio(it))} Para mudar nelas, escolha a unidade acima.</span>
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}

      <Dialog open={!!editando} onOpenChange={(v) => { if (!v && !salvando) setEditando(null); }}>
        <DialogContent className="max-w-2xl rounded-2xl border-none p-0" {...protegeDoModalGlobal}>
          {editando && (
            <EditorMensagem key={`${editando.canal}-${editando.chave}-${unidade}`}
                            item={editando} unidade={unidade} nivelLbl={nivelLbl} salvando={salvando} wa={wa}
                            onSalvar={async (texto) => {
                              if (!String(texto).trim()) {
                                if (await voltarPadrao(editando)) setEditando(null);
                                return;
                              }
                              if (!(await confirmaPadraoGlobal(editando))) return;
                              if (await salvar(editando, texto)) { modal.success("Mensagem salva."); setEditando(null); }
                            }}
                            onVoltarPadrao={async () => { if (await voltarPadrao(editando)) setEditando(null); }}
                            onCancelar={() => setEditando(null)} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EditorMensagem({ item, unidade, nivelLbl, salvando, wa, onSalvar, onVoltarPadrao, onCancelar }) {
  const proprio = textoDoNivel(item, unidade);
  const inicial = proprio || item.texto_efetivo || item.texto_padrao || "";
  const [texto, setTexto] = useState(inicial);
  const [previa, setPrevia] = useState(null);
  const [erroPrevia, setErroPrevia] = useState("");
  const area = useRef(null);
  const seq = useRef(0);

  const variaveis = variaveisDoItem(item);
  const obrigatorias = gruposObrigatorios(item);
  const { erro, avisos } = validarTexto(texto, item);
  const vazio = !texto.trim();
  const alterado = texto !== inicial;
  // Em branco só faz sentido quando este nível tem texto próprio (aí é o "Voltar ao padrão").
  const podeSalvar = !erro && alterado && !(vazio && !proprio);
  const sms = item.canal === "sms" ? smsInfo(texto) : null;
  const max = limiteTexto(item);

  // Prévia pelo servidor (mesmo render do envio), 400 ms depois da última tecla. Em branco mostra o
  // texto que passaria a valer.
  useEffect(() => {
    if (erro) { setPrevia(null); return undefined; }
    const minha = ++seq.current;
    const t = setTimeout(async () => {
      try {
        const corpo = { chave: item.chave, canal: item.canal, unit_id: unidade || null, texto: vazio ? textoSemPersonalizacao(item, unidade) : texto };
        const { data } = await masterApi.post("/master/mensagens/preview", corpo);
        if (minha !== seq.current) return;
        setPrevia(data || null);
        setErroPrevia("");
      } catch (e) {
        if (minha !== seq.current) return;
        setPrevia(null);
        setErroPrevia(erroApiMensagens(e));
      }
    }, 400);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [texto, erro]);

  const inserir = (nome) => {
    const el = area.current;
    const r = inserirNoCursor(texto, `{${nome}}`, el?.selectionStart, el?.selectionEnd);
    setTexto(r.texto);
    setTimeout(() => {
      try { if (el) { el.focus(); el.setSelectionRange(r.cursor, r.cursor); } } catch (_) { /* ignora */ }
    }, 0);
  };

  const borda = erro ? "border-rose-400 focus:border-rose-500" : (!vazio ? "border-emerald-400 focus:border-emerald-500" : "border-neutral-200 focus:border-[color:var(--brand-blue)]");
  const avisosServidor = Array.isArray(previa?.avisos) ? previa.avisos.filter(Boolean) : [];

  return (
    <div className="bg-white p-5 rounded-2xl space-y-3" data-testid="msg-editor">
      <DialogHeader>
        <DialogTitle className="font-serif-display text-xl pr-6">{item.rotulo || item.chave}</DialogTitle>
      </DialogHeader>
      <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-[11.5px] text-neutral-600">
        <div><span className="text-neutral-400">Canal:</span> {CANAL_LBL[item.canal] || item.canal}</div>
        <div><span className="text-neutral-400">Onde vale:</span> {nivelLbl}</div>
        {item.gatilho && <div className="sm:col-span-2"><span className="text-neutral-400">Quando sai:</span> {item.gatilho}</div>}
      </div>
      {item.canal === "whatsapp" && (
        <div className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] text-amber-900" data-testid="msg-editor-aviso-oficial">
          <Info size={12} className="shrink-0 mt-0.5" />
          <span>
            {item.oficial?.observacao || "No WhatsApp oficial sai o template aprovado pela Meta; este texto vale para o provedor antigo."}
            {wa && (
              <span className="block mt-0.5 font-semibold" data-testid="msg-editor-provedor">
                Em uso agora: {wa.rotulo}.{" "}
                {wa.textosValem ? "Este texto é o que sai." : "Este texto fica sem efeito enquanto for assim."}
              </span>
            )}
          </span>
        </div>
      )}
      {item.observacao && <p className="text-[11px] text-neutral-500" data-testid="msg-editor-obs">{item.observacao}</p>}
      {!unidade && unidadesComTextoProprio(item).length > 0 && (
        <div className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] text-amber-900" data-testid="msg-editor-proprias">
          <AlertTriangle size={12} className="shrink-0 mt-0.5" />
          <span>{textoUnidadesProprias(unidadesComTextoProprio(item))} Este padrão vale só para as unidades sem texto próprio.</span>
        </div>
      )}

      <div>
        <label className="text-[10px] uppercase tracking-widest text-neutral-500">Texto</label>
        <textarea ref={area} data-testid="msg-editor-texto" value={texto} rows={6}
                  onChange={(e) => setTexto(e.target.value)}
                  placeholder={textoSemPersonalizacao(item, unidade)}
                  className={`mt-1 w-full rounded-lg bg-white border px-3 py-2 text-[12.5px] leading-snug outline-none focus:ring-2 focus:ring-blue-500/15 ${borda}`} />
        <div className="flex flex-wrap items-center gap-1 mt-1" data-testid="msg-editor-variaveis">
          <span className="text-[10.5px] text-neutral-500 mr-1">{variaveis.length ? "Variáveis:" : "Esta mensagem não usa variáveis."}</span>
          {variaveis.map((v) => (
            <button key={v.nome} type="button" data-testid={`msg-var-${v.nome}`} title={v.descricao || undefined}
                    onClick={() => inserir(v.nome)}
                    className="px-1.5 h-6 rounded-md border border-neutral-200 bg-white text-[10.5px] font-mono hover:bg-neutral-50">
              {`{${v.nome}}`}
            </button>
          ))}
        </div>
        {variaveis.some((v) => v.descricao) && (
          <ul className="mt-1 text-[10.5px] text-neutral-500 grid sm:grid-cols-2 gap-x-3">
            {variaveis.filter((v) => v.descricao).map((v) => <li key={v.nome}><span className="font-mono">{`{${v.nome}}`}</span>: {v.descricao}</li>)}
          </ul>
        )}
        <div className="mt-1 text-[10.5px]" data-testid="msg-editor-contador">
          {sms ? (
            <span className={sms.passou ? "text-rose-600 font-semibold" : "text-neutral-500"}>
              SMS sem acentos: {textoTamanhoSms(sms.caracteres)} · as variáveis mudam o tamanho final
            </span>
          ) : (
            <span className={texto.trim().length > max ? "text-rose-600 font-semibold" : "text-neutral-500"}>
              {texto.length}/{max} caracteres
            </span>
          )}
        </div>
        {obrigatorias.length > 0 && (
          <p className="text-[10.5px] text-neutral-500 mt-1" data-testid="msg-editor-obrigatorias">
            Obrigatório: {obrigatorias.map((g) => (g.length === 1 ? `{${g[0]}}` : `uma destas ${listaVars(g)}`)).join("; ")}.
          </p>
        )}
        {erro && <p className="text-[11.5px] text-rose-700 mt-1" data-testid="msg-editor-erro">{erro}</p>}
        {/* Avisos imediatos só até a prévia do servidor chegar (ela traz os mesmos e mais). */}
        {!previa && avisos.map((a) => <p key={a} className="text-[11px] text-amber-800 mt-1" data-testid="msg-editor-aviso">{a}</p>)}
        {vazio && (
          <p className="text-[11px] text-neutral-500 mt-1" data-testid="msg-editor-vazio">
            Em branco: volta ao padrão ({descricaoVoltarPadrao(item, unidade).toLowerCase()}).
          </p>
        )}
      </div>

      <div className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 px-3 py-2">
        <div className="text-[9.5px] uppercase tracking-widest text-neutral-500 mb-1">Prévia (valores de exemplo)</div>
        {erroPrevia
          ? <div className="text-[11.5px] text-rose-700" data-testid="msg-previa-erro">{erroPrevia}</div>
          : (
            <>
              <div className="text-[12px] whitespace-pre-wrap text-neutral-800 break-words" data-testid="msg-previa">
                {erro ? "Corrija o texto para ver a prévia." : (previa?.texto_renderizado ?? "Gerando prévia...")}
              </div>
              {previa && !erro && (
                <div className="text-[10.5px] text-neutral-500 mt-1" data-testid="msg-previa-info">
                  {previa.caracteres != null && (item.canal === "sms"
                    ? <>No SMS: {textoTamanhoSms(previa.caracteres)}</>
                    : <>{previa.caracteres} caracteres</>)}
                </div>
              )}
              {previa?.texto_sms && !erro && (
                <div className="mt-1.5" data-testid="msg-previa-sms">
                  <div className="text-[9.5px] uppercase tracking-widest text-neutral-500">Como sai no SMS</div>
                  <div className="text-[11.5px] font-mono whitespace-pre-wrap break-words text-neutral-700">{previa.texto_sms}</div>
                </div>
              )}
              {!erro && avisosServidor.map((a) => (
                <p key={a} className="text-[11px] text-amber-800 mt-1" data-testid="msg-previa-aviso">{a}</p>
              ))}
            </>
          )}
      </div>

      <div className="flex flex-wrap gap-2 pt-1">
        <button type="button" data-testid="msg-editor-salvar" onClick={() => onSalvar(texto)}
                disabled={!podeSalvar || salvando}
                className="btn-action btn-salvar">
          <Save size={14} /> {salvando ? "Salvando..." : "Salvar"}
        </button>
        {proprio && (
          <button type="button" data-testid="msg-editor-padrao" onClick={onVoltarPadrao} disabled={salvando}
                  className="btn-ghost h-10 px-3 text-[12px] font-medium inline-flex items-center gap-1.5" style={{ borderRadius: 8 }}>
            <RotateCcw size={13} /> Voltar ao padrão
          </button>
        )}
        <button type="button" data-testid="msg-editor-cancelar" onClick={onCancelar} disabled={salvando}
                className="btn-ghost h-10 px-3 text-[12px] font-medium ml-auto" style={{ borderRadius: 8 }}>
          Cancelar
        </button>
      </div>
    </div>
  );
}
